import { supabase } from '@/integrations/supabase/client';

/**
 * Get user profile avatar URL
 */
export async function getProfileAvatar(userId: string): Promise<string | null> {
  const { data, error } = await supabase
    .from('profiles')
    .select('avatar_url')
    .eq('user_id', userId)
    .single();

  if (error) throw error;
  return data?.avatar_url || null;
}

/**
 * Update profile avatar (upload and update)
 */
export async function updateProfileAvatar(
  userId: string,
  file: File
): Promise<{ url: string; path: string }> {
  // Validate file
  if (!file.type.startsWith('image/')) {
    throw new Error('File must be an image');
  }

  if (file.size > 5 * 1024 * 1024) {
    throw new Error('Image must be less than 5MB');
  }

  // Generate unique filename
  const fileExt = file.name.split('.').pop();
  const fileName = `avatars/${userId}-${Date.now()}.${fileExt}`;

  // Upload to storage
  const { data: uploadData, error: uploadError } = await supabase.storage
    .from('avatars')
    .upload(fileName, file, {
      cacheControl: '3600',
      upsert: true,
    });

  if (uploadError) throw uploadError;

  // Get public URL
  const {
    data: { publicUrl },
  } = supabase.storage.from('avatars').getPublicUrl(uploadData.path);

  // Update profile with new avatar URL
  const { error: updateError } = await supabase
    .from('profiles')
    .update({ avatar_url: publicUrl })
    .eq('user_id', userId);

  if (updateError) throw updateError;

  return {
    url: publicUrl,
    path: uploadData.path,
  };
}

/**
 * Update profile fields
 */
export async function updateProfile(
  userId: string,
  updates: {
    full_name?: string;
    email?: string;
  }
): Promise<void> {
  const { error } = await supabase
    .from('profiles')
    .update(updates)
    .eq('user_id', userId);

  if (error) throw error;
}
